/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
// Extends from
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
// Ember imports
import { get, set, action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
// External imports
import DataType from '@linc-technologies/linced-common/enums/data-type';
import GroupType from '@linc-technologies/linced-common/enums/group-type';
import Options from '@linc-technologies/linced-common/enums/options';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import config from 'linced-app/config/environment';
import moment from 'moment';
import { filterValue } from '@linc-technologies/linced-common/utils/filtering';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import {
  filterBy,
  findBy,
  mapBy,
  sortBy,
} from '@linc-technologies/linced-common/utils/arrays';

export default class BulkPublishController extends Controller {
  @action
  setSendNotifications(value) {
    this.sendNotifications = value;
  }

  @action
  setUseCustomDisplayDate(value) {
    this.useCustomDisplayDate = value;
  }

  // Services
  @service
  fetch;

  @service
  options;

  @service
  permissions;

  @service
  router;

  @service
  session;

  @service
  store;

  @service
  strings;

  @service
  toaster;

  GroupType = GroupType;

  @computed('groupId')
  get groupType() {
    let group = this.store.peekRecord('group', this.groupId);
    return group.type;
  }

  @tracked
  selectedPosts = [];
  @tracked
  post = null;
  @tracked
  showPostContent = false;
  @tracked sortProp = 'title';
  @tracked sortDir = 'asc';
  @tracked
  filters = [];
  @tracked
  postPublishingInProgress = false;
  @tracked
  customDisplayDate = null;
  @tracked
  useCustomDisplayDate = false;
  @tracked
  sendNotifications = true;
  @tracked
  publishDialogueOpen = false;
  @optionAlias(Options.NOTIFICATIONS_EMAIL_ENABLED)
  emailNotificationsEnabled;
  @optionAlias(Options.NOTIFICATIONS_APP_ENABLED)
  pushNotificationsEnabled;
  @optionAlias(Options.NOTIFICATIONS_EMAIL_POSTS)
  postEmailNotificationsEnabled;
  @optionAlias(Options.NOTIFICATIONS_APP_POSTS)
  postPushNotificationsEnabled;

  // publishablePosts is an array of visible posts a user is allowed to publish
  @computed('model.[]')
  get publishablePosts() {
    if (isEmpty(this.model)) {
      return [];
    }
    return this.model.filter((post) => {
      if (this.permissions.equal(Scopes.TENANT_STAFF)) {
        // Staff members can publish posts that are:
        // - made by students
        // - their own
        // - made by staff members and coming from pages that have a staffCanPublish flag set to true
        let isTheAuthor =
          post.get('author.id') === this.get('session.personId');
        if (isTheAuthor) {
          return true;
        }
        let authorIsStudent = post.get('author.type') === PersonType.LEARNER;
        if (authorIsStudent) {
          return true;
        }
        return post.get('page.staffCanPublish') ?? false;
      }
      return post;
    });
  }

  @computed(
    'emailNotificationsEnabled',
    'pushNotificationsEnabled',
    'postEmailNotificationsEnabled',
    'postPushNotificationsEnabled',
  )
  get postNotificationsEnabled() {
    return (
      (this.emailNotificationsEnabled && this.postEmailNotificationsEnabled) ||
      (this.pushNotificationsEnabled && this.postPushNotificationsEnabled)
    );
  }

  @computed(
    'publishablePosts.[]',
    'sortProp',
    'sortDir',
    'filters.@each.{field,value}',
  )
  get filteredPosts() {
    let sortedPosts = sortBy(this.publishablePosts, this.sortProp);
    if (this.sortDir === 'desc') {
      sortedPosts.reverse();
    }
    if (isEmpty(this.filters)) {
      // No filters - just return the data.
      return sortedPosts;
    }

    let filters = this.filters.map((filter) => {
      let dataType = DataType.STRING;
      let key = filter.field;
      switch (filter.field) {
        case 'firstName':
          key = 'person.firstName';
          break;
        case 'lastName':
          key = 'person.lastName';
          break;
        case 'class':
          key = 'person.homeroom.label';
          break;
        case 'year':
          key = 'person.currentYearLevel';
          break;
        case 'page':
          key = 'page.title';
          break;
        case 'title':
          key = 'subject';
          break;
        case 'author':
          key = 'author.fullName';
          break;
      }

      return {
        key,
        dataType,
        value: filter.value,
      };
    });

    // Apply all filters to each row in one hit, to avoid iterating over a (potentially) large dataset multiple times.
    return sortedPosts.filter((row) => {
      return filters.every((filter) => {
        return filterValue(
          get(row, filter.key) ?? '',
          filter.dataType,
          filter.value,
        );
      });
    });
  }

  @computed('publishablePosts.[]', 'selectedPosts.[]')
  get postCounts() {
    const totals = this.publishablePosts.reduce((array, obj) => {
      const page = obj.get('page.label');
      array[page] = array[page] || { total: 0, selected: 0 };
      array[page].total++;
      return array;
    }, {});
    this.selectedPosts.forEach((obj) => {
      const page = obj.get('page.label');
      totals[page].selected++;
    });
    return totals;
  }

  @computed('selectedPosts.[]')
  get numNotifications() {
    return this.selectedPosts.reduce((num, post) => {
      let contacts = post.get('person.learner.contacts');
      if (isEmpty(contacts)) {
        return num;
      }
      return num + filterBy(contacts, 'allowedAccess').length;
    }, 0);
  }

  init() {
    super.init(...arguments);
    this.customDisplayDate = {
      date: moment().format('YYYY-MM-DD'),
      time: moment().format('HH:mm'),
    };
  }

  @action
  publish() {
    this.postPublishingInProgress = true;
    let url = `${config.APP.apiHost}/posts/${config.APP.apiPath}/posts/:bulkpublish`;
    let data = {
      postIds: mapBy(this.selectedPosts, 'id'),
      sendNotifications:
        this.postNotificationsEnabled && this.sendNotifications,
    };
    if (this.useCustomDisplayDate) {
      data.displayDate = moment(
        this.customDisplayDate.date + ' ' + this.customDisplayDate.time,
      ).unix();
    }
    return this.fetch
      .post(url, { data })
      .then(() => {
        this.postPublishingInProgress = false;
        this.publishDialogueOpen = false;
        this.selectedPosts = [];
        this.toaster.success('Posts published successfully.');
        this.model.update();
      })
      .catch(() => {
        this.toaster.error('Error publishing posts. Please try again.');
      });
  }

  @action
  filter(field, value) {
    let filter = findBy(this.filters, 'field', field);
    if (isEmpty(filter)) {
      // No existing filter on this field; add it.
      this.filters = [...this.filters, { field, value }];
      return;
    }

    if (isEmpty(value)) {
      // Filter has been cleared; remove it.
      this.filters = this.filters.filter((f) => f !== filter);
    } else {
      // Existing filter has been changed; update it.
      set(filter, 'value', value);
    }
  }

  @action
  togglePublishDialog() {
    this.publishDialogueOpen = !this.publishDialogueOpen;
  }

  @action
  togglePostSelected(post) {
    if (this.selectedPosts.includes(post)) {
      this.selectedPosts = this.selectedPosts.filter((p) => p !== post);
    } else {
      this.selectedPosts = [...this.selectedPosts, post];
    }
  }

  @action
  onChangeSort({ sortProp, sortDir }) {
    this.sortProp = sortProp;
    this.sortDir = sortDir;
  }

  @action
  selectAllPosts() {
    if (this.selectedPosts.length === this.filteredPosts.length) {
      return (this.selectedPosts = []);
    }
    return (this.selectedPosts = this.filteredPosts.slice());
  }

  @action
  togglePostContent(post) {
    if (this.showPostContent) {
      this.showPostContent = false;
      this.post = null;
    } else {
      this.showPostContent = true;
      this.post = post;
    }
  }

  @action
  setCustomDisplayDate(type, param) {
    this.set(
      `customDisplayDate.${type}`,
      type === 'date' ? moment(param).format('YYYY-MM-DD') : param,
    );
  }

  @action
  backToAllPosts() {
    let params = {
      groupId: this.groupId,
      proofreadPosts: this.proofreadPosts,
      notProofread: this.notProofread,
      pageIds: JSON.stringify(this.selectedPageIds),
      authorIds: JSON.stringify(this.selectedAuthorIds),
      startDate: this.startDate,
      endDate: this.endDate,
      draftPosts: this.draftPosts,
      publishedPosts: this.publishedPosts,
      staffOnly: this.staffOnly,
    };
    this.router.transitionTo('group.posts', { queryParams: params });
  }
}
